import { BULK_DOWNLOAD_STATUS } from '@/features/purchasehistory/types/bulk-download-detail.types'

/**
 * DEV-7273: shared BulkDownload terminal-status predicates, ported from Vue 2
 * (Scripts/vue/common/bulkDownloadStatus.js).
 *
 * They live in one place because the CompletedWithErrors (104) gap appeared in BOTH Vue 2
 * detail screens for the same reason: each knew only COMPLETED and FAILED, so a 104 job
 * polled forever and rendered "In Progress" indefinitely. Vue 3 inherited that gap when the
 * screen was ported.
 */

/**
 * Terminal = the server will not change this status again. Use ONLY to decide whether to keep
 * polling. Includes FAILED, so it must NOT drive `downloadComplete` — see isDeliveredStatus.
 */
export function isTerminalStatus(status: number | undefined | null): boolean {
	return (
		status === BULK_DOWNLOAD_STATUS.COMPLETED ||
		status === BULK_DOWNLOAD_STATUS.FAILED ||
		status === BULK_DOWNLOAD_STATUS.COMPLETED_WITH_ERRORS
	)
}

/**
 * Delivered = terminal AND something was produced, so the result panels, stat tiles and the
 * download button may render. Drives `downloadComplete`.
 *
 * FAILED is EXCLUDED deliberately. Widening this to every terminal status would make
 * `downloadComplete` true for a hard-failed export, which renders the green "Finished" chip
 * and enables the download button for a file that was never produced.
 */
export function isDeliveredStatus(status: number | undefined | null): boolean {
	return (
		status === BULK_DOWNLOAD_STATUS.COMPLETED ||
		status === BULK_DOWNLOAD_STATUS.COMPLETED_WITH_ERRORS
	)
}

/**
 * Hard failure = the export produced nothing usable. Drives `technicalError`, which renders
 * the red "A technical error occurred" alert and SUPPRESSES the result panels.
 *
 * 104 is deliberately EXCLUDED: a CompletedWithErrors export may have delivered most of its
 * batches, so its results must still render. Its messaging is carried by isCompletedWithErrors.
 */
export function isHardFailureStatus(status: number | undefined | null): boolean {
	return status === BULK_DOWNLOAD_STATUS.FAILED
}

/**
 * Completed-with-errors = terminal, partially successful. Drives the amber status strip and
 * chip. Keyed on the status, NOT on FailedLeads: the scheduler never writes that counter, so
 * any FailedLeads-based branch is dead for scheduler-managed jobs.
 */
export function isCompletedWithErrors(status: number | undefined | null): boolean {
	return status === BULK_DOWNLOAD_STATUS.COMPLETED_WITH_ERRORS
}
